import { useEffect, useRef, useState } from "react";
import { useConvexAuth, useQuery } from "convex/react";
import type { Doc } from "../../convex/_generated/dataModel";
import { api } from "../../convex/_generated/api";

type AppUser = Doc<"users">;
const NO_PROFILE_GRACE_MS = 1200;

export type AppAuthState =
  | { status: "loading_convex_auth" }
  | { status: "unauthenticated" }
  | { status: "authenticated_profile_loading" }
  | { status: "authenticated_no_profile" }
  | { status: "authenticated_inactive"; user: AppUser }
  | { status: "authenticated_ready"; user: AppUser };

export function useAppAuthState(): AppAuthState {
  const { isLoading, isAuthenticated } = useConvexAuth();
  const currentUser = useQuery(api.users.getCurrentUser);
  const authenticatedAtRef = useRef<number | null>(null);
  const [, forceProfileRecheck] = useState(0);

  useEffect(() => {
    if (!isAuthenticated) {
      authenticatedAtRef.current = null;
      return;
    }

    if (authenticatedAtRef.current === null) {
      authenticatedAtRef.current = Date.now();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || currentUser !== null) {
      return;
    }

    const startedAt = authenticatedAtRef.current ?? Date.now();
    const elapsed = Date.now() - startedAt;

    if (elapsed >= NO_PROFILE_GRACE_MS) {
      return;
    }

    const timeoutId = setTimeout(() => {
      forceProfileRecheck((tick) => tick + 1);
    }, NO_PROFILE_GRACE_MS - elapsed);

    return () => clearTimeout(timeoutId);
  }, [isAuthenticated, currentUser]);

  if (isLoading) {
    return { status: "loading_convex_auth" };
  }

  if (!isAuthenticated) {
    return { status: "unauthenticated" };
  }

  if (currentUser === undefined) {
    return { status: "authenticated_profile_loading" };
  }

  if (currentUser === null) {
    const startedAt = authenticatedAtRef.current ?? Date.now();

    if (Date.now() - startedAt < NO_PROFILE_GRACE_MS) {
      return { status: "authenticated_profile_loading" };
    }

    return { status: "authenticated_no_profile" };
  }

  if (!currentUser.isActive) {
    return { status: "authenticated_inactive", user: currentUser };
  }

  return { status: "authenticated_ready", user: currentUser };
}
